import { Autocomplete, TextField } from '@mui/material'

import { type Manifest, useListComponentsQuery } from '../api/authoring'
import { LISTING_LAG } from '../model/labels'
import { typeProse } from '../model/manifest'

/**
 * The control for a field holding a component's id.
 *
 * **It is reached by a marker, never by a member name.** The schema says
 * `x-component-role`, the way it says `x-link` and `x-slot`, and this
 * offers the tenant's components of that role. That is the whole reason
 * the marker carries a *role* rather than a type: the client resolves it
 * against the manifest, so a tenant-declared type taking that role is
 * offered here without this file learning its name — the same property
 * that lets a `richText` field get an editor without a list of field
 * names in the client.
 *
 * **A picker rather than a text box, because the alternative is a UUID.**
 * A bare id is the one string an author cannot write: it is not derived
 * from anything they typed, and a wrong one is not a typo away from
 * correct — it is a link to a page that does not exist. This is the same
 * argument `x-link` already makes for a placement.
 *
 * The stored id is offered back even when the listing does not hold it,
 * so a value the author cannot see is never silently blanked — the call
 * the enum control already makes about a value the build stopped
 * declaring.
 */
export function ComponentRefField({
  label,
  role,
  tenant,
  manifest,
  value,
  onChange,
  disabled,
  required,
}: {
  label: string
  role: string
  tenant: string
  manifest: Manifest | undefined
  value: string
  onChange: (next: string) => void
  disabled?: boolean
  required?: boolean
}) {
  // Role to types, from the manifest, because the platform's roles are a
  // fixed set and which types take one is a per-tenant fact.
  const types = (manifest?.types ?? []).filter((declared) => declared.role === role)

  // The listing is deliberately untyped. `record` is the first role more
  // than one tenant type ordinarily takes, so asking for its first type
  // would make every later record type invisible. The endpoint's untyped
  // listing is the one list that can be narrowed by the manifest without
  // inventing a client-side merge of one request per type.
  const { data, isLoading, error } = useListComponentsQuery(
    { tenant, limit: 100 },
    { skip: types.length === 0 },
  )

  const components = (data?.components ?? []).filter((component) => types.some((type) => type.name === component.type))
  const options = components.map((component) => component.id)
  const known = value && !options.includes(value) ? [value, ...options] : options
  const labelOf = (id: string) => {
    const found = components.find((component) => component.id === id)
    if (!found) return id
    return found.label || typeProse(manifest, found.type).title || id
  }

  return (
    <Autocomplete
      options={known}
      value={value || null}
      onChange={(_, next) => onChange(next ?? '')}
      getOptionLabel={labelOf}
      disabled={disabled || types.length === 0}
      loading={isLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={types.length === 0
            ? 'There is nothing of that kind to choose yet.'
            : error
            ? 'Could not read the list. The stored value is unchanged.'
            : LISTING_LAG}
        />
      )}
      // The id is what is stored and what a reader has to be able to
      // check, so it is shown beside the name rather than hidden behind
      // it. An author picks by name; a person debugging reads the id.
      renderOption={(props, id) => {
        const { key, ...rest } = props as
          & { key?: string }
          & Record<string, unknown>
        return (
          <li key={key ?? id} {...rest}>
            <span>{labelOf(id)}</span>
            <span
              style={{
                marginLeft: 8,
                opacity: 0.6,
                fontFamily: 'ui-monospace, monospace',
                fontSize: '0.8em',
              }}
            >
              {id}
            </span>
          </li>
        )
      }}
    />
  )
}
